import { FC, useState, useEffect, useContext } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import _ from "lodash";
import moment from "moment";
import { Trash2 } from "react-feather";
import { useFieldArray, useForm } from "react-hook-form";
import companyApi from "../../../api/companyApi";
import { AppContext } from "../../../context";
import { Company, Equipment } from "../../../types/Company";
import { Department } from "../../../types/Department";
import { Gender } from "../../../types/Gender";
import { User, WorktimeGroup } from "../../../types/User";
import { formatMinutes } from "../../../utils/time";

interface Props {
  company: Company;
  visible: boolean;
  onVisibleChange: (val: boolean) => void;
}

interface UserForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  gender: Gender;
  roleId: User["roleId"];
  dateBirth: string;
  citizenship: string;
  ssn: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  position: string;
  costPool: string;
  employmentPost: string;
  managerId: string;
  employmentNature: string;
  employmentStart: string;
  trialPeriodEnd: string;
  employmentMunicipality: string;
  employeeNumber: string;
  iban: string;
  bic: string;
  userFields: { value: string; fieldId: string; titleEn: string }[];
  equipment: { equipmentId: string; note: string }[];
  salaries: { type: "fixed" | "hourly"; date: string; salary: number }[];
  languageProficiencies: { language: string; level: string }[];
  holidaysPerMonth: number;
  incrementedHolidays: number;
  availableHolidays: number;
  worktimeGroupId: string;
  departmentId: string;
  subDepartmentId: string;
}

const CreateUserModal: FC<Props> = ({ company, visible, onVisibleChange }) => {
  const { showToast } = useContext(AppContext);
  const [loading, toggleLoading] = useState(false);
  const [existingUsers, setExistingUsers] = useState<User[]>([]);
  const [availableEquipment, setEquipment] = useState<Equipment[]>([]);
  const [worktimeGroups, setWorkimeGroups] = useState<WorktimeGroup[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);

  const { setValue, watch, getValues, reset, control, register, handleSubmit } = useForm<UserForm>();

  const equipmentFields = useFieldArray({
    control,
    name: "equipment",
  });
  const salaryFields = useFieldArray({
    control,
    name: "salaries",
  });
  const languageFields = useFieldArray({
    control,
    name: "languageProficiencies",
  });

  useEffect(() => {
    const fetchUserFields = async () => {
      try {
        const { data } = await companyApi.getImportData(company.id);

        setExistingUsers(data.existingUsers);
        setEquipment(data.equipment);
        setWorkimeGroups(data.worktimeGroups);
        setDepartments(data.departments);

        setValue(
          "userFields",
          data.userFields.map((uf) => {
            return {
              fieldId: uf.id,
              value: "",
              titleEn: uf.titleEn,
            };
          }),
        );
      } catch {
        showToast({
          title: "Fetch failed",
          message: "Could not fetch import data",
          variant: "danger",
        });
      }
    };
    if (visible) {
      fetchUserFields();
      reset();
    }
  }, [visible]);

  const onSubmit = async (data: UserForm) => {
    toggleLoading(true);
    try {
      const {
        data: { userId },
      } = await companyApi.importUser(company.id, {
        email: data.email.toLowerCase().trim(),
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        gender: data.gender.trim() || null,
        citizenship: data.citizenship.trim() || null,
        dateBirth: data.dateBirth.trim() || null,
        ssn: data.ssn.trim() || null,
        roleId: Number(data.roleId) as 1 | 2 | 3,
        managerId: data.managerId.trim() || null,
        iban: data.iban.trim() || null,
        bic: data.bic.trim() || null,
        employeeNumber: data.employeeNumber.trim() || null,
        address: data.address.trim() || null,
        postalCode: data.postalCode.trim() || null,
        city: data.city.trim() || null,
        country: data.country.trim() || null,
        phone: data.phone.trim() || null,
        employmentNature: data.employmentNature.trim() || null,
        employmentMunicipality: data.employmentMunicipality.trim() || null,
        employmentPost: data.employmentPost.trim() || null,
        employmentStart: data.employmentStart.trim() || null,
        trialPeriodEnd: data.trialPeriodEnd.trim() || null,
        position: data.position.trim() || null,
        costPool: data.costPool.trim() || null,
        equipment:
          data.equipment?.map((e) => ({
            equipmentId: e.equipmentId,
            note: e.note.trim(),
          })) || [],
        userFields:
          data.userFields
            ?.filter((f) => !!f.value.trim())
            .map((f) => ({
              fieldId: f.fieldId,
              value: f.value.trim(),
            })) || [],
        salaries:
          data.salaries?.map((s) => ({
            date: s.date,
            salary: Number(s.salary),
            type: s.type,
          })) || [],
        languageProficiencies:
          data.languageProficiencies?.map((l) => ({
            language: l.language,
            level: l.level,
          })) || [],
        holidaysPerMonth: Number(data.holidaysPerMonth),
        availableHolidays: Number(data.availableHolidays),
        incrementedHolidays: Number(data.incrementedHolidays),
        worktimeGroupId: data.worktimeGroupId || null,
        departmentId: data.departmentId || null,
        subDepartmentId: data.subDepartmentId || null,
      });
      showToast({
        title: "Success",
        message: `User id: ${userId}`,
        variant: "success",
      });
      onVisibleChange(false);
    } catch {
      showToast({
        title: "Create failed",
        message: "Could not create user",
        variant: "danger",
      });
    }
    toggleLoading(false);
  };

  const subDepartments = departments.find((d) => watch("departmentId") === d.id)?.subDepartments;

  return (
    <Modal show={visible} onHide={() => onVisibleChange(false)} size="xl">
      <Modal.Header closeButton={true} className="d-flex justify-content-between">
        <Modal.Title className="flex-grow-1">Create user</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Row>
            <h6>User info</h6>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  required
                  {...register("email")}
                  onFocus={async () => {
                    const text = await navigator.clipboard.readText();
                    if (text.includes("@")) {
                      const name = text.split("@")[0];
                      if (name) {
                        const nameSplit = name.split(".");
                        if (nameSplit.length === 2) {
                          setValue("firstName", _.upperFirst(nameSplit[0]));
                          setValue("lastName", _.upperFirst(nameSplit[1]));
                          setValue("email", text);
                          setValue("citizenship", "Suomi");
                        }
                      }
                    }
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>First name</Form.Label>
                <Form.Control required {...register("firstName")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Last name</Form.Label>
                <Form.Control required {...register("lastName")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Gender</Form.Label>
                <Form.Control as="select" {...register("gender")}>
                  <option value={""}></option>
                  <option value={"male"}>Male</option>
                  <option value={"female"}>Female</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Citizenship</Form.Label>
                <Form.Control {...register("citizenship")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Date birth</Form.Label>
                <Form.Control {...register("dateBirth")} type="date" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>SSN</Form.Label>
                <Form.Control
                  {...register("ssn")}
                  onFocus={async () => {
                    const text = await navigator.clipboard.readText();
                    const birth = text.split("-")[0];
                    if (birth.length === 6) {
                      const str = String(birth).padStart(6, "0");
                      const day = str.slice(0, 2);
                      const month = str.slice(2, 4);
                      const year = str.slice(4, 6);

                      // Use moment with explicit format
                      const date = moment(`${day}${month}${year}`, "DDMMYY");

                      setValue("dateBirth", date.format("YYYY-MM-DD"));
                      setValue("ssn", text);
                    }
                  }}
                />
              </Form.Group>
            </Col>

            <hr />
            <h6>Management</h6>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Department</Form.Label>
                <Form.Control
                  as="select"
                  {...register("departmentId")}
                  onChange={(e) => {
                    setValue("departmentId", e.target.value);
                    setValue("subDepartmentId", "");
                  }}>
                  <option value={""}></option>
                  {departments.map((d) => {
                    return (
                      <option key={d.id} value={d.id}>
                        {d.title}
                      </option>
                    );
                  })}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Sub department</Form.Label>
                <Form.Control as="select" disabled={!watch("departmentId")} {...register("subDepartmentId")}>
                  <option value={""}></option>
                  {subDepartments?.map((d) => {
                    return (
                      <option key={d.id} value={d.id}>
                        {d.title}
                      </option>
                    );
                  })}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Role</Form.Label>
                <Form.Control as="select" required {...register("roleId")}>
                  <option value={""}></option>
                  <option value={1}>Admin</option>
                  <option value={2}>Manager</option>
                  <option value={3}>Employee</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Manager</Form.Label>
                <Form.Control as="select" {...register("managerId")}>
                  <option value={""}>No manager</option>
                  {existingUsers?.map((u) => {
                    return (
                      <option value={u.id} key={u.id}>
                        {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
                      </option>
                    );
                  })}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Position</Form.Label>
                <Form.Control {...register("position")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>IBAN</Form.Label>
                <Form.Control {...register("iban")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>BIC</Form.Label>
                <Form.Control {...register("bic")} />
              </Form.Group>
            </Col>
            <hr />
            <h6>Contact info</h6>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control {...register("address")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Postal code</Form.Label>
                <Form.Control {...register("postalCode")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>City</Form.Label>
                <Form.Control {...register("city")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Country</Form.Label>
                <Form.Control {...register("country")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control {...register("phone")} />
              </Form.Group>
            </Col>

            <hr />
            <h6>Employment</h6>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Worktime group</Form.Label>
                <Form.Control as="select" {...register("worktimeGroupId")}>
                  <option value={""}>No group</option>
                  {worktimeGroups.map((g) => {
                    return (
                      <option value={g.id} key={g.id}>
                        {g.title}, {formatMinutes(g.weeklyWorkMinutes)} ({g.status})
                      </option>
                    );
                  })}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Employment nature</Form.Label>
                <Form.Control as="select" {...register("employmentNature")}>
                  <option value={""}></option>
                  <option value={"full-time"}>Full time</option>
                  <option value={"part-time"}>Part time</option>
                  <option value={"hourly"}>Hourly</option>
                  <option value={"freelancer"}>Freelanders</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Employment start date</Form.Label>
                <Form.Control
                  {...register("employmentStart")}
                  type="date"
                  onBlur={() => {
                    const start = moment(getValues("employmentStart"), "YYYY-MM-DD");
                    if (start.isValid()) {
                      setValue("trialPeriodEnd", start.add(6, "months").subtract(1, "day").format("YYYY-MM-DD"));
                    }
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Trial period end</Form.Label>
                <Form.Control {...register("trialPeriodEnd")} type="date" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Employment municipality</Form.Label>
                <Form.Control {...register("employmentMunicipality")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cost pool</Form.Label>
                <Form.Control {...register("costPool")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Employment post</Form.Label>
                <Form.Control {...register("employmentPost")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Employee number</Form.Label>
                <Form.Control {...register("employeeNumber")} />
              </Form.Group>
            </Col>
            <hr />
            <h6>Absences</h6>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Available holidays</Form.Label>
                <Form.Control type="number" min={0} {...register("availableHolidays")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Incremented holidays</Form.Label>
                <Form.Control type="number" min={0} {...register("incrementedHolidays")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Holidays per month</Form.Label>
                <Form.Control type="number" min={0} {...register("holidaysPerMonth")} />
              </Form.Group>
            </Col>
            <hr />
            <h6>Company user fields</h6>
            {watch("userFields")?.map((uf, i) => {
              return (
                <Col md={6} key={uf.fieldId}>
                  <Form.Group className="mb-3">
                    <Form.Label>{uf.titleEn}</Form.Label>
                    <Form.Control
                      value={uf.value}
                      onChange={(e) => {
                        setValue(`userFields.${i}.value`, e.target.value);
                      }}
                    />
                  </Form.Group>
                </Col>
              );
            })}
            <hr />
            <div className="d-flex align-items-center">
              <h6 className="mb-0">Equipment ({watch("equipment")?.length || 0})</h6>
              <Button
                size="sm"
                variant="outline-primary"
                className="ms-2"
                onClick={() => {
                  equipmentFields.append({ equipmentId: "", note: "" });
                }}>
                Add equipment
              </Button>
            </div>
            <div className="d-flex flex-wrap p-1">
              {equipmentFields.fields.map((f, i) => {
                return (
                  <div key={f.id} className="d-flex align-items-end mb-2 border rounded p-2 m-1">
                    <Form.Group className="me-2">
                      <Form.Label>{"Equipment"}</Form.Label>
                      <Form.Control as="select" required {...register(`equipment.${i}.equipmentId`)}>
                        <option disabled value={""}></option>
                        {availableEquipment?.map((e) => {
                          return (
                            <option value={e.id} key={e.id}>
                              {e.title}
                            </option>
                          );
                        })}
                      </Form.Control>
                    </Form.Group>
                    <Form.Group className="me-2">
                      <Form.Label>{"Note"}</Form.Label>
                      <Form.Control required {...register(`equipment.${i}.note`)} />
                    </Form.Group>

                    <Button variant="outline-danger" size="sm" onClick={() => equipmentFields.remove(i)}>
                      <Trash2 />
                    </Button>
                  </div>
                );
              })}
            </div>
            <hr />
            <div className="d-flex align-items-center">
              <h6 className="mb-0">Salaries ({watch("salaries")?.length || 0})</h6>
              <Button
                size="sm"
                variant="outline-primary"
                className="ms-2"
                onClick={() => {
                  salaryFields.append({
                    date: moment()
                      .subtract(watch("salaries")?.length || 0, "days")
                      .format("YYYY-MM-DD"),
                    salary: 0,
                    type: "fixed",
                  });
                }}>
                Add salary
              </Button>
            </div>
            <div className="d-flex flex-wrap p-1">
              {salaryFields.fields.map((f, i) => {
                return (
                  <div key={f.id} className="d-flex align-items-end mb-2 border rounded p-2 m-1">
                    <Form.Group className="me-2">
                      <Form.Label>{"Date"}</Form.Label>
                      <Form.Control required type="date" {...register(`salaries.${i}.date`)} />
                    </Form.Group>
                    <Form.Group className="me-2">
                      <Form.Label>{"Type"}</Form.Label>
                      <Form.Control required as="select" {...register(`salaries.${i}.type`)}>
                        <option value={"fixed"}>Fixed</option>
                        <option value={"hourly"}>Hourly</option>
                      </Form.Control>
                    </Form.Group>
                    <Form.Group className="me-2">
                      <Form.Label>{"Salary"}</Form.Label>
                      <Form.Control required step={"0.01"} type="number" {...register(`salaries.${i}.salary`)} />
                    </Form.Group>

                    <Button variant="outline-danger" size="sm" onClick={() => salaryFields.remove(i)}>
                      <Trash2 />
                    </Button>
                  </div>
                );
              })}
            </div>
            <hr />
            <div className="d-flex align-items-center">
              <h6 className="mb-0">Languages ({watch("languageProficiencies")?.length || 0})</h6>
              <Button
                size="sm"
                variant="outline-primary"
                className="ms-2"
                onClick={() => {
                  languageFields.append({ language: "", level: "" });
                }}>
                Add language
              </Button>
            </div>
            <div className="d-flex flex-wrap p-1">
              {languageFields.fields.map((f, i) => {
                return (
                  <div key={f.id} className="d-flex align-items-end mb-2 border rounded p-2 m-1">
                    <Form.Group className="me-2">
                      <Form.Label>{"Language"}</Form.Label>
                      <Form.Control required {...register(`languageProficiencies.${i}.language`)} />
                    </Form.Group>
                    <Form.Group className="me-2">
                      <Form.Label>{"Level"}</Form.Label>
                      <Form.Control required as="select" {...register(`languageProficiencies.${i}.level`)}>
                        <option value={""}></option>
                        <option value={"mother-tongue"}>Mother tongue</option>
                        <option value={"excellent"}>Excellent</option>
                        <option value={"good"}>Good</option>
                        <option value={"average"}>Average</option>
                        <option value={"basics"}>Basics</option>
                      </Form.Control>
                    </Form.Group>

                    <Button variant="outline-danger" size="sm" onClick={() => languageFields.remove(i)}>
                      <Trash2 />
                    </Button>
                  </div>
                );
              })}
            </div>
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ position: "sticky", bottom: 0 }} className="bg-white">
          <Button variant="primary" disabled={loading} type="submit">
            Create user
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateUserModal;
